import { useState, useEffect } from 'react';
import { X, FloppyDisk, CircleNotch, EnvelopeSimple, Funnel } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { API } from './constants';

/**
 * Edit modal voor een BESTAANDE campagne — wijzigt verzendadres,
 * handtekening-override en zichtbare naam zonder de campagne opnieuw
 * te hoeven maken.
 *
 * Gebruikt `PUT /api/email/campaigns/{campaign_id}` met partial payload.
 * Mailbox-lijst komt van `GET /api/marketing/mailboxes` zodat dropdown
 * altijd up-to-date is met de profile-actieve adressen.
 */
export function CampaignEditModal({ campaign, onClose, onSaved }) {
  const [mailboxes, setMailboxes] = useState([]);
  const [dimensions, setDimensions] = useState([]);
  const [segmentCount, setSegmentCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: campaign?.name || '',
    sender_email: campaign?.sender_email || '',
    sender_name: campaign?.sender_name || '',
    signature_html: campaign?.signature_html || '',
    segment_filter: campaign?.segment_filter || {},
  });

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/marketing/mailboxes`, { credentials: 'include' }).then(r => r.ok ? r.json() : { mailboxes: [] }),
      fetch(`${API}/profiles/filter-config`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
    ])
      .then(([mbx, cfg]) => {
        setMailboxes(mbx.mailboxes || []);
        const dims = cfg?.facility_filter_dimensions || [];
        setDimensions(Array.isArray(dims) ? dims : []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Live segment-count voor de huidige filter — toont impact direct.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/email/segments/count`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form.segment_filter || {}),
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled && d) setSegmentCount(d.count); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [form.segment_filter]);

  // Sync form wanneer de campagne-prop verandert (kan voorkomen als de
  // gebruiker direct van campagne-rij A naar B klikt).
  useEffect(() => {
    setForm({
      name: campaign?.name || '',
      sender_email: campaign?.sender_email || '',
      sender_name: campaign?.sender_name || '',
      signature_html: campaign?.signature_html || '',
      segment_filter: campaign?.segment_filter || {},
    });
  }, [campaign?.campaign_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleDimensionValue = (dimKey, value) => {
    setForm(prev => {
      const sf = { ...(prev.segment_filter || {}) };
      const dims = { ...(sf.dimensions || {}) };
      const cur = Array.isArray(dims[dimKey]) ? [...dims[dimKey]] : [];
      const idx = cur.indexOf(value);
      if (idx >= 0) cur.splice(idx, 1); else cur.push(value);
      if (cur.length) dims[dimKey] = cur; else delete dims[dimKey];
      if (Object.keys(dims).length) sf.dimensions = dims; else delete sf.dimensions;
      return { ...prev, segment_filter: sf };
    });
  };

  const onMailboxChange = (email) => {
    const m = mailboxes.find(x => x.email === email);
    setForm(prev => ({
      ...prev,
      sender_email: email,
      // Reset custom signature en vul standaard sender_name van mailbox in.
      signature_html: '',
      sender_name: m?.from_name || prev.sender_name || '',
    }));
  };

  const save = async () => {
    if (!form.sender_email) {
      toast.error('Kies een verzendadres');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/email/campaigns/${campaign.campaign_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          sender_email: form.sender_email,
          sender_name: form.sender_name || null,
          signature_html: form.signature_html || null,
          segment_filter: form.segment_filter || {},
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(`Opslaan mislukt: ${err.detail || res.statusText}`);
        return;
      }
      toast.success('Campagne bijgewerkt');
      onSaved?.();
    } catch (e) {
      toast.error(`Netwerkfout: ${e.message || 'onbekend'}`);
    }
    setSaving(false);
  };

  if (!campaign) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[80] flex items-center justify-center p-4"
      onClick={() => !saving && onClose()}
      data-testid="campaign-edit-modal"
    >
      <div
        className="bg-white w-full max-w-xl max-h-[88vh] flex flex-col shadow-2xl border border-zinc-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-zinc-200 gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 bg-blue-50 border border-blue-200 flex items-center justify-center shrink-0">
              <EnvelopeSimple size={18} className="text-[#0055FF]" />
            </div>
            <div className="min-w-0">
              <h3 className="font-heading text-base font-semibold text-zinc-900">Campagne bewerken</h3>
              <p className="text-[11px] text-zinc-500 mt-0.5 font-data truncate">{campaign.campaign_id}</p>
            </div>
          </div>
          <button
            onClick={() => !saving && onClose()}
            disabled={saving}
            data-testid="campaign-edit-close"
            className="p-1.5 hover:bg-zinc-100 disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div>
            <label className="text-xs font-medium text-zinc-500 mb-1 block">Campagnenaam</label>
            <Input
              value={form.name}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              className="rounded-sm text-sm"
              data-testid="campaign-edit-name"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-zinc-500 mb-1 block">
              Verzendadres
              <a href="/settings/mailboxes" className="ml-2 text-[11px] text-[#0055FF] hover:underline">+ beheren</a>
            </label>
            {loading ? (
              <div className="text-xs text-zinc-400 flex items-center gap-2">
                <CircleNotch size={12} className="animate-spin" /> Mailboxes laden...
              </div>
            ) : (
              <Select value={form.sender_email} onValueChange={onMailboxChange}>
                <SelectTrigger className="rounded-sm text-sm" data-testid="campaign-edit-sender-trigger">
                  <SelectValue placeholder="Kies afzender..." />
                </SelectTrigger>
                <SelectContent className="z-[90]">
                  {mailboxes.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-zinc-400">Geen mailboxes geconfigureerd</div>
                  ) : mailboxes.map(m => (
                    <SelectItem key={m.mailbox_id || m.email} value={m.email} data-testid={`campaign-edit-mailbox-option-${m.email}`}>
                      <span className="font-medium">{m.email}</span>
                      {m.is_default && <span className="ml-2 text-[10px] text-amber-700">(standaard)</span>}
                      {m.display_name && <span className="ml-1 text-[11px] text-zinc-500">— {m.display_name}</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {form.sender_email && (
              <p className="text-[11px] text-zinc-400 mt-1.5">
                Handtekening van <strong>{form.sender_email}</strong> wordt automatisch gebruikt — tenzij je hieronder een eigen handtekening invult.
              </p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-zinc-500 mb-1 block">
              Zichtbare naam (From-name) <span className="text-[10px] text-zinc-400 ml-1">— optioneel</span>
            </label>
            <Input
              value={form.sender_name}
              onChange={(e) => setForm(f => ({ ...f, sender_name: e.target.value }))}
              placeholder="Bijv: Martijn van PoolFIX"
              className="rounded-sm text-sm"
              data-testid="campaign-edit-sender-name"
            />
          </div>

          <div>
            <label className="text-xs font-medium text-zinc-500 mb-1 block">
              Aangepaste handtekening (HTML)
              <span className="text-[10px] text-zinc-400 ml-1">— optioneel, overschrijft mailbox-handtekening</span>
            </label>
            <textarea
              data-testid="campaign-edit-signature"
              value={form.signature_html}
              onChange={(e) => setForm(f => ({ ...f, signature_html: e.target.value }))}
              rows={5}
              placeholder="Laat leeg voor de standaard handtekening van het verzendadres"
              className="w-full px-3 py-2 text-xs font-mono border border-zinc-200 rounded-sm focus:outline-none focus:border-[#0F172A] resize-none"
            />
          </div>

          {/* Doelgroep — AI-classificatie dimensies */}
          {dimensions.length > 0 && (
            <div className="border-t border-zinc-100 pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <Funnel size={12} className="text-purple-600" />
                  <label className="text-xs font-medium text-zinc-700">Doelgroep filter</label>
                </div>
                <div className="text-[11px]">
                  <span className="font-data text-sm font-bold text-purple-600" data-testid="campaign-edit-segment-count">
                    {segmentCount === null ? '...' : segmentCount}
                  </span>
                  <span className="text-zinc-500 ml-1">contacten</span>
                </div>
              </div>
              {dimensions.map(dim => {
                const sel = form.segment_filter?.dimensions?.[dim.key] || [];
                const selSet = new Set(Array.isArray(sel) ? sel : [sel]);
                return (
                  <div key={dim.key} data-testid={`edit-dim-${dim.key}`}>
                    <div className="text-[10px] font-medium text-zinc-500 mb-1.5 uppercase tracking-wider">{dim.label || dim.key}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {(dim.options || []).map(opt => {
                        const isSel = selSet.has(opt.value);
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => toggleDimensionValue(dim.key, opt.value)}
                            data-testid={`edit-dim-${dim.key}-${opt.value}`}
                            className={`px-2 py-0.5 text-[11px] border transition-colors ${isSel ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-zinc-700 border-zinc-200 hover:bg-purple-50'}`}
                          >
                            {opt.label || opt.value}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              <p className="text-[10px] text-zinc-400">Geen knop geselecteerd binnen een rij = die dimensie wordt niet meegefilterd. Combineer rijen voor AND-logica.</p>
            </div>
          )}

          {campaign.status === 'active' && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2">
              <strong>Let op:</strong>
              <span>Deze campagne is <strong>actief</strong>. Wijzigingen worden direct toegepast op alle nog te versturen e-mails in de queue.</span>
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-zinc-200 bg-zinc-50/60 flex items-center justify-end gap-2">
          <button
            onClick={() => !saving && onClose()}
            disabled={saving}
            className="px-3 py-1.5 text-xs text-zinc-600 hover:bg-zinc-100 transition-colors border border-zinc-200 disabled:opacity-50"
            data-testid="campaign-edit-cancel"
          >
            Annuleer
          </button>
          <button
            onClick={save}
            disabled={saving || !form.sender_email}
            className="px-3 py-1.5 text-xs font-medium bg-[#0055FF] text-white hover:bg-[#0044DD] disabled:opacity-50 transition-colors flex items-center gap-1.5"
            data-testid="campaign-edit-save"
          >
            {saving ? <CircleNotch size={12} className="animate-spin" /> : <FloppyDisk size={12} />}
            {saving ? 'Opslaan...' : 'Opslaan'}
          </button>
        </div>
      </div>
    </div>
  );
}
